import {isTextAnnotation} from './annotationAnchors';
import type {AnnotationModeState} from './AnnotationModeStore';
import type {Annotation, AnnotationType, HighlightColor} from './annotationTypes';

// Zustand v5 compares snapshots by reference, so the empty case must be one array.
const NO_ANNOTATIONS: Annotation[] = [];

/** A region's marks as the student has asked to see them: none while hidden. */
export function selectVisibleRegionAnnotations(
  state: AnnotationModeState,
  regionId: string
): Annotation[] {
  return state.areAnnotationsHidden
    ? NO_ANNOTATIONS
    : (state.annotationsByRegionId[regionId] ?? NO_ANNOTATIONS);
}

/** Reads what is stored, not what is shown: hiding must not unmount the way back. */
export function selectHasAnyAnnotations(state: AnnotationModeState): boolean {
  return Object.values(state.annotationsByRegionId).some((annotations) => annotations.length > 0);
}

function findAnnotation(
  state: AnnotationModeState,
  regionId: string,
  id: string | undefined
): Annotation | undefined {
  if (!id) {
    return undefined;
  }
  return state.annotationsByRegionId[regionId]?.find((annotation) => annotation.id === id);
}

/** The color of the annotation with `id` in `regionId`, or undefined if absent. */
export function selectHighlightColor(
  state: AnnotationModeState,
  regionId: string,
  id: string | undefined
): HighlightColor | undefined {
  return findAnnotation(state, regionId, id)?.color;
}

/** The style type of the annotation with `id` in `regionId`, or undefined if absent. */
export function selectAnnotationType(
  state: AnnotationModeState,
  regionId: string,
  id: string | undefined
): AnnotationType | undefined {
  return findAnnotation(state, regionId, id)?.type;
}

/** The note of the annotation with `id` in `regionId`, or undefined if absent. */
export function selectAnnotationNote(
  state: AnnotationModeState,
  regionId: string,
  id: string | undefined
): string | undefined {
  return findAnnotation(state, regionId, id)?.note;
}

/**
 * The marks carrying a note. Allocates, so it belongs in a `useMemo` over the
 * region's bucket — a store selector returning this would never stabilize.
 */
export function filterNotedAnnotations(annotations: Annotation[]): Annotation[] {
  return annotations.filter((annotation) => annotation.note !== undefined);
}

/**
 * Whether the mark still carries a note. A blank note is a deleted note, so a
 * note UI listing it unmounts — and an editor anchored to that row would be left
 * measuring a detached node.
 */
export function selectIsNoteListed(
  state: AnnotationModeState,
  regionId: string | undefined,
  id: string | undefined
): boolean {
  if (!regionId) {
    return false;
  }
  return findAnnotation(state, regionId, id)?.note !== undefined;
}

/** One edge answers for every mark: their shared color, or yellow once they differ. */
export function selectSupplementBorderColor(annotations: Annotation[]): HighlightColor | null {
  const [first] = annotations;
  if (!first) {
    return null;
  }
  return annotations.every(({color}) => color === first.color) ? first.color : 'yellow';
}

/**
 * Noted marks in reading order, since they share one indicator. Allocates, so it
 * belongs in a `useMemo` (see `filterNotedAnnotations`).
 */
export function selectNotedMarksInOrder(annotations: Annotation[]): Annotation[] {
  return filterNotedAnnotations(annotations).sort((a, b) =>
    isTextAnnotation(a) && isTextAnnotation(b) ? a.anchor.start - b.anchor.start : 0
  );
}

const SUPPLEMENT_NOTE_LABEL_BY_TYPE: Record<string, string> = {
  image: '(selected image)',
  audio: '(selected audio clip)',
  'free-form': '(selected text section)'
};

/**
 * What heads a whole-supplement note: the student-visible name where one exists (a
 * document card shows its name as its title), otherwise the supplement's kind.
 */
export function supplementNoteLabel(
  mountedSupplements: AnnotationModeState['mountedSupplements'],
  regionId: string
): string {
  const supplement = mountedSupplements.find((mounted) => mounted.regionId === regionId);
  if (supplement?.type === 'free-form' && supplement.name.trim()) {
    return supplement.name;
  }
  return SUPPLEMENT_NOTE_LABEL_BY_TYPE[supplement?.type ?? ''] ?? 'Supplement';
}

/** True while any mounted region has annotatable content. */
export function selectHasAnnotatableContent(state: AnnotationModeState): boolean {
  return state.annotatableContentMounts > 0;
}
